// BookDetails.js
import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import API_BASE_URL from '../config'
import {
  Container,
  Typography,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  useMediaQuery,
  useTheme,
  Button,
  Grid,
  IconButton,
  Divider,
  TextField,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useChatbotActions } from '../contexts/ChatbotContext'

function BookDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { updateCurrentBook, openChatbot } = useChatbotActions()
  const lastBookIdRef = useRef(null)

  const [book, setBook] = useState(null)
  const [reviewText, setReviewText] = useState('')
  const [logEntry, setLogEntry] = useState('')
  const [status, setStatus] = useState('want to read')
  const [ratingValue, setRatingValue] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const token = localStorage.getItem('token')

  useEffect(() => {
    if (id && id !== 'new') {
      const fetchBook = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/books/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          setBook(res.data)
          setReviewText(res.data.review || '')
          setStatus(res.data.status || 'want to read')
          setRatingValue(res.data.rating || 0)
        } catch (err) {
          console.error('Error fetching book:', err)
        }
      }
      fetchBook()
    } else if (location.state && location.state.book) {
      const bookData = location.state.book
      setBook({
        googleId: bookData.id,
        title: bookData.volumeInfo.title,
        authors: bookData.volumeInfo.authors || ['Unknown Author'],
        description: bookData.volumeInfo.description || 'No description available.',
        thumbnail:
          (bookData.volumeInfo.imageLinks?.thumbnail || bookData.volumeInfo.imageLinks?.smallThumbnail || '')
            .replace(/^http:/, 'https:'),
        logs: [],
      })
    } else {
      console.error('No book data available')
      navigate('/')
    }
  }, [id, location.state, navigate, token])

  // Auto-update global chatbot context when book loads
  useEffect(() => {
    if (book) {
      const bookId = book._id || book.id || book.googleId
      if (bookId && lastBookIdRef.current !== bookId) {
        lastBookIdRef.current = bookId
        updateCurrentBook(book)
      }
    }

    // Cleanup: Clear the current book when leaving this page
    return () => {
      updateCurrentBook(null)
    }
  }, [book, updateCurrentBook])

  const submitBookAndNavigate = async () => {
    try {
      if (id === 'new') {
        const bookData = {
          googleId: book.googleId,
          title: book.title,
          authors: book.authors,
          description: book.description,
          thumbnail: book.thumbnail,
          review: reviewText,
          status,
          rating: ratingValue,
          logs: logEntry.trim() ? [{ entry: logEntry }] : [],
        }
        const res = await axios.post(`${API_BASE_URL}/books/add`, bookData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        navigate(`/book/${res.data._id}`, { replace: true })
      } else {
        await axios.post(
          `${API_BASE_URL}/books/review/${id}`,
          { review: reviewText },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        if (logEntry.trim()) {
          await axios.post(
            `${API_BASE_URL}/books/logs/${id}`,
            { entry: logEntry },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          setLogEntry('')
        }
        await axios.patch(
          `${API_BASE_URL}/books/status/${id}`,
          { status },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        await axios.patch(
          `${API_BASE_URL}/books/rating/${id}`,
          { rating: ratingValue },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        navigate('/')
      }
    } catch (err) {
      console.error('Error submitting book:', err)
    }
  }

  const deleteBook = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/books/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      navigate('/my-books')
    } catch (err) {
      console.error('Error deleting book:', err)
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false)
    deleteBook()
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  if (!book) return <Typography>Loading...</Typography>

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        {/* Header with Back Button and Title */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: '#8D7B68',
              '&:hover': { backgroundColor: 'rgba(141, 123, 104, 0.1)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {book.title}
          </Typography>
        </Box>

        {/* Main Grid Layout */}
        <Grid container spacing={3}>
          {/* Left Sidebar - Book Cover (Sticky on Desktop) */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: { md: 'sticky' },
                top: { md: 24 },
                backgroundColor: 'transparent',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '2/3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid #8D7B68',
                  }}
                >
                <CardMedia
                  component="img"
                  image={(book.thumbnail && book.thumbnail.replace(/^http:/, 'https:')) || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="450"%3E%3Crect width="300" height="450" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="24" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E'}
                  alt={book.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="450"%3E%3Crect width="300" height="450" fill="%23F4ECE3"/%3E%3Ctext x="50%25" y="50%25" font-family="Georgia,serif" font-size="24" fill="%238D7B68" text-anchor="middle" dominant-baseline="middle"%3ENo Cover%3C/text%3E%3C/svg%3E'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Content - Book Info */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                backgroundColor: 'transparent',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                border: '1px solid #8D7B68',
              }}
            >
              {/* Author */}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: '#8D7B68',
                  mb: 2,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                }}
              >
                by {book.authors?.join(', ') || 'Unknown Author'}
              </Typography>

              {/* Description */}
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6, mb: 3 }}>
                {book.description}
              </Typography>

              <Divider sx={{ my: 3, borderColor: '#8D7B68' }} />

              {/* Status Selector */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="status-label">Reading Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status-select"
                  value={status}
                  label="Reading Status"
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{
                    backgroundColor: 'rgba(141, 123, 104, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8D7B68',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#7A6859',
                    },
                  }}
                >
                <MenuItem value="want to read">Want to Read</MenuItem>
                <MenuItem value="currently reading">Currently Reading</MenuItem>
                <MenuItem value="finished">Finished</MenuItem>
                <MenuItem value="dropped">Dropped</MenuItem>
              </Select>
            </FormControl>

              {/* Rating */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    mb: 1,
                  }}
                >
                  My Rating
                </Typography>
                <Rating
                  name="book-rating"
                  value={ratingValue}
                  onChange={(event, newValue) => setRatingValue(newValue)}
                  precision={0.5}
                  sx={{
                    '& .MuiRating-icon': {
                      color: '#F4B400',
                    },
                  }}
                />
              </Box>

              {/* Review */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="My Review"
                  multiline
                  rows={4}
                  fullWidth
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Write your thoughts about this book..."
                  sx={{
                    backgroundColor: 'rgba(141, 123, 104, 0.05)',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#8D7B68',
                      },
                      '&:hover fieldset': {
                        borderColor: '#7A6859',
                      },
                    },
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#8D7B68',
                    '&:hover': { backgroundColor: '#7A6859' },
                    color: '#fff',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    flex: 1,
                  }}
                  onClick={submitBookAndNavigate}
                >
                  {id === 'new' ? 'Add Book' : 'Update Book'}
                </Button>

                {id !== 'new' && (
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#b71c1c',
                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                      },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      flex: { xs: 1, sm: 'none' },
                    }}
                    onClick={handleDeleteClick}
                  >
                    Delete Book
                  </Button>
                )}
              </Box>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
            >
              <DialogTitle id="delete-dialog-title">Delete Book</DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-dialog-description">
                  Are you sure you want to delete this book from your collection? This action
                  cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteCancel}>Cancel</Button>
                <Button onClick={handleDeleteConfirm} color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default BookDetails
